import { Page, PageBody, ToastContainer } from '@pypestream/design-system';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from '@pypestream/translations';
import { isProduction } from '@pypestream/utils';
import * as Sentry from '@sentry/react';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Outlet,
  Route,
  useLocation,
  useNavigate,
  RouterProvider as ReactRouterProvider,
} from 'react-router';

import { Loader, PrimaryNav, ErrorBoundary } from './components';
import { centeredContainerStyles } from './components/loader';
import { AccessSetup } from './components/access-setup';
import { RootBody } from './components/root-body';
import { SolutionOpenFailureModal } from './components/solution-open-failure/solution-open-failure-modal';
import {
  StudioPageProps,
  useAgentVocabulary,
  useCurrentRoute,
  useLoadingState,
  useResetScrollOnRouteChange,
  useProStudioRoutes,
  useTableStoreSync,
  usePublishStatusWatcherForCoworkers,
  useValidationModeToast,
  useAccessGrantedToastOnOpen,
  useSolutionDeletedSync,
} from './hooks';
import { useKnowledgeSetup } from './pages/talk/use-knowledge-setup';
import {
  AdvancedSettings,
  DataPrivacy,
  LiveAgentPlatforms,
  ProjectSettings,
  ReportingApiCredentials,
} from './pages';
import { DEFAULT_PROJECT_SETTINGS_SECTION } from './pages/project-settings';
import { ErrorPage } from './pages/error-page';
import { resolvePageLoadView } from './hooks/loading-states';
import { syncXstateWithRouteParams } from './utils';
import {
  sendUserEvent,
  useProStudioCtxSelector,
  useProStudioStateMatches,
  useUserStateMatches,
} from './xstate/app.xstate';

const Root: FC = () => {
  useAgentVocabulary(); // Project Bobcat, Proposal 1 — flag-gated copy overlay
  useTableStoreSync();
  useKnowledgeSetup(); // Talk it through's keys + knowledge base, after create
  usePublishStatusWatcherForCoworkers();
  useValidationModeToast();
  useSolutionDeletedSync(); // PSTUDIO-3671
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const {
    home: { path: homePath },
  } = useProStudioRoutes();

  const isAccessDenied = useProStudioStateMatches('accessDenied');
  const isSolutionNotFound = useProStudioStateMatches('solutionNotFound'); // PSTUDIO-3671
  // PSTUDIO-4098. The access screen below arms the "Access granted" toast on a
  // grant; it is shown here, once the project has actually loaded, because the
  // screen itself unmounts as soon as the machine leaves `accessDenied`.
  const isProjectOpen = useProStudioStateMatches(
    'checkingUserInfo.projectFlow.projectDataLoaded'
  );
  const armAccessGrantedToast = useAccessGrantedToastOnOpen(isProjectOpen);

  const { projectId } = useProStudioCtxSelector((ctx) => ({
    projectId: ctx.project?.id,
  }));

  useResetScrollOnRouteChange();

  useEffect(() => {
    if (pathname === '/' && homePath) {
      navigate(homePath);
    }
  }, [pathname, projectId, homePath, navigate]);

  // PSTUDIO-3671: the open solution was deleted (in this or another tab, or on
  // another machine) → the machine routed to #solutionNotFound (toast shown on
  // entry). Redirect to All Solutions (`/:projectId`, botId dropped). The
  // machine PARKS in #solutionNotFound — this same-project redirect is not a
  // "meaningful change" for syncXstateWithRouteParams, so it dispatches no
  // setParams and the machine does NOT leave the state on its own. That is why
  // #solutionNotFound is in NON_LOADING_STUDIO_STATES: the rendered All
  // Solutions page must show its content, not the full-page loader, while
  // parked. The machine only moves on once the user navigates to a solution.
  useEffect(() => {
    if (isSolutionNotFound && homePath) {
      navigate(homePath);
    }
  }, [isSolutionNotFound, homePath, navigate]);

  return (
    <ErrorBoundary>
      <Page>
        <PrimaryNav />

        <RootBody
          main={
            isAccessDenied ? (
              // Keyed by project: a different project in the URL starts a fresh
              // assign → wait → re-read cycle instead of reusing this one's state.
              <AccessSetup
                key={projectId ?? ''}
                onGranted={armAccessGrantedToast}
              />
            ) : (
              <Outlet />
            )
          }
          // Mounted here, not in a page: an open ps-modal moves its element
          // into a portal (`ModalWC._openModal` → `update-portal-content`), and
          // a page wrapper that unmounts on navigation would then removeChild
          // a node that is no longer its child. Root does not unmount between
          // pages. RootBody covers the other direction (insertBefore).
          overlay={<SolutionOpenFailureModal />}
        />
        <ToastContainer stacked position="bottom-right" />
      </Page>
    </ErrorBoundary>
  );
};

// Mirrors manager (router.tsx), so both apps wrap the factory the same way.
//
// LIMIT, so nobody reads more into this than it does: in @sentry/react v10 the
// wrapper is a no-op unless `reactRouterBrowserTracingIntegration` is the active
// tracing integration — it needs `useEffect`/`useLocation`/`useNavigationType`/
// `matchRoutes` handed to it, and `browserTracingIntegration()` (what
// `sentryInit` installs) does not. Until that integration is wired, route names
// stay raw URLs. Navigation breadcrumbs come from `breadcrumbsIntegration`,
// which is on by default and unrelated to this wrapper.
const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouter(createBrowserRouter);

export const RouterProvider = () => {
  const routes = useProStudioRoutes();
  // Router must be created exactly once, in a state initializer (useMemo is only
  // a cache and may be recomputed). Recreating it on every render (which happens
  // whenever XState ctx changes, since useProStudioRoutes reads ctx) causes
  // in-flight navigations to be lost — visible as the URL updating but the
  // rendered route staying on the previous page until refresh.
  // Route schemas and elements are static, so freezing the initial routes
  // object is safe.
  //
  // `useTransitions={false}` keeps router updates out of React.startTransition,
  // which React Router 7 otherwise uses by default. The XState selectors here are
  // plain setState subscriptions, so under a transition every machine update made
  // during a navigation commits first, against the OLD route params — e.g. the
  // channels page reacting to a newly selected survey solution by navigating to
  // the previous solution's versions page. This restores the single synchronous
  // commit React Router 6 had.
  const [router] = useState(() =>
    sentryCreateBrowserRouter(
      createRoutesFromElements(
        <Route
          path="/"
          element={<Root />}
          loader={({ params, request }) => {
            const url = new URL(request.url);
            const debugValue = url.searchParams.get('debug');

            let debugMode: boolean | undefined = undefined;

            if (debugValue === 'on' || debugValue === 'true') {
              debugMode = true;
            } else if (debugValue === 'off' || debugValue === 'false') {
              debugMode = false;
            } else {
              debugMode = !isProduction;
            }

            syncXstateWithRouteParams({
              params,
              url: new URL(request.url),
              debugMode,
            });
            return null;
          }}
          shouldRevalidate={() => {
            return true;
          }}
        >
          <Route index element={routes.home.element} />
          {Object.values(routes).map(({ schema: path, element, label }) => (
            <Route path={path} element={element} key={label} />
          ))}
          {/* Project Settings is a nested layout route (persistent side-nav
                + <Outlet/>), so it's declared explicitly rather than via the
                flat route map. */}
          <Route path=":projectId/settings" element={<ProjectSettings />}>
            <Route
              index
              element={
                <Navigate to={DEFAULT_PROJECT_SETTINGS_SECTION.value} replace />
              }
            />
            <Route
              path="live-agent-platforms"
              element={<LiveAgentPlatforms />}
            />
            <Route
              path="reporting-api-credentials"
              element={<ReportingApiCredentials />}
            />
            <Route path="data-privacy" element={<DataPrivacy />} />
            <Route path="advanced-settings" element={<AdvancedSettings />} />
          </Route>
        </Route>
      )
    )
  );

  return <ReactRouterProvider router={router} useTransitions={false} />;
};

export function WithLoadingState<T extends StudioPageProps>(
  WrappedComponent: React.ComponentType<T>
) {
  const displayName =
    WrappedComponent.displayName || WrappedComponent.name || 'Component';

  const ComponentWithLoadingState = (props: T) => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const { label: pageLabel } = useCurrentRoute();
    const [t] = useTranslation();
    const currentPageDataLoading = useLoadingState() || isLoading;
    const userInfoFailed = useUserStateMatches('loggedIn.error');
    const view = resolvePageLoadView({
      loading: currentPageDataLoading,
      userInfoFailed,
    });

    // PSTUDIO-4099 S30: a failed GetUserInfo used to leave this on the loader
    // for good. `user.refetch` re-enters `loggedIn.loading`, and on success
    // `getUserData` (still subscribed, since it never ran) sends
    // `updateUserInfo`, which moves the machine on.
    if (view === 'user-info-error') {
      return (
        <ErrorPage
          label={String(t('pro-studio/common:userInfoLoadError.title'))}
          body={String(t('pro-studio/common:userInfoLoadError.body'))}
          buttonText={String(t('pro-studio/common:userInfoLoadError.retry'))}
          buttonOnClick={() => sendUserEvent({ type: 'user.refetch' })}
        />
      );
    }

    return (
      <>
        <div
          style={{
            ...centeredContainerStyles,
            display: currentPageDataLoading ? 'flex' : 'none',
          }}
        >
          <Loader relative />
        </div>
        <PageBody
          {...props.pageBodyProps}
          style={{
            display: currentPageDataLoading ? 'none' : 'block',
          }}
        >
          <WrappedComponent
            {...props}
            label={pageLabel}
            loading={currentPageDataLoading}
            setLoading={setIsLoading}
          />
        </PageBody>
      </>
    );
  };

  ComponentWithLoadingState.displayName = `WithLoadingState(${displayName})`;

  return ComponentWithLoadingState;
}
